import { ProjectElementType } from '@deltek/specification-client/core/Models/projectElement'
import { useElementItemShallowSelector } from 'components/WorkspaceEditor/ElementItemStore'
import { useSuggestionsShallowSelector } from 'components/WorkspaceEditor/SuggestionStore'
import { useShallowSelector } from 'components/WorkspaceEditor/Workspace2Store'
import { TrackedChange } from 'models/changes'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import {
    useSuggestionCardFilterHandler,
    useTrackChangeCards,
} from '../../hooks'
import { useTrackChangesCardStyles } from '../../styles'
import { getSuggestionTime } from '../../utils/TrackChangesAndSuggestionsUtils'
import { LoadingCardSkeletons } from './LoadingCardsSkeleton'
import { NoChangesMessage } from './NoChangesMessage'
import { VirtualizedSuggestionList } from './VirtualizedSuggestionList/VirtualizedSuggestionList'

/**
 * Hook to detect when new items are added to a list and trigger a callback
 */
const useDetectNewItems = <T,>(items: T[], onNewItemsAdded: () => void) => {
    const prevCountRef = useRef(0)

    useEffect(() => {
        const currentCount = items.length
        const prevCount = prevCountRef.current

        // Only trigger callback if items increased
        if (currentCount > prevCount && currentCount > 0) {
            onNewItemsAdded()
        }

        prevCountRef.current = currentCount
    }, [items.length, onNewItemsAdded])
}

/**
 * CardListComponent renders a list of track change cards based on the provided track changes data.
 * It uses memoization to optimize performance and avoid unnecessary re-renders.
 * @param {Record<string, TrackedChange>} props.trackChangesData - The track changes data to be displayed in the card list.
 * @return {JSX.Element} The rendered card list component.
 * */
export const CardList = React.memo(
    ({
        trackChangesData,
    }: {
        trackChangesData: Record<string, TrackedChange>
    }) => {
        useSuggestionCardFilterHandler()
        const trackChangeCardsResult = useTrackChangeCards() || {}
        const {
            shouldShowLoadingCards = false,
            isNotDefaultSuggestionCardFilter = false,
            suggestionsData = {},
            isUnresolvedSuggestionCardFilter = false,
        } = trackChangeCardsResult
        const { setResetSuggestionScroll } = useSuggestionsShallowSelector(
            (state) => ({
                setResetSuggestionScroll: state.setResetSuggestionScroll,
            })
        )
        const isTreeLoading =
            useShallowSelector((s) => s?.isTreeLoading) ?? false
        const elementType =
            useElementItemShallowSelector(
                (state) => state?.elementItemValue?.elementType
            ) ?? ''
        const flattenedSuggestions = useMemo(() => {
            // Flatten all suggestions into a single array
            const suggestions = Object.values(suggestionsData).flatMap(
                (group) => group?.suggestions || []
            )

            return [...suggestions].sort(
                (a, b) =>
                    getSuggestionTime(b, isUnresolvedSuggestionCardFilter) -
                    getSuggestionTime(a, isUnresolvedSuggestionCardFilter)
            )
        }, [suggestionsData, isUnresolvedSuggestionCardFilter])

        const classes = useTrackChangesCardStyles({
            isNotDefaultSuggestionCardFilter,
        })
        const isAssemblyElement = useMemo(
            () =>
                (elementType ?? '').toUpperCase() ===
                ProjectElementType.ASSEMBLY,
            [elementType]
        )
        const shouldShowEmpty = useMemo(
            () =>
                (flattenedSuggestions?.length ?? 0) === 0 &&
                !(shouldShowLoadingCards ?? false) &&
                !(isTreeLoading ?? false),
            [
                flattenedSuggestions?.length,
                shouldShowLoadingCards,
                isTreeLoading,
            ]
        )

        // Use custom hook to detect new suggestions and trigger scroll reset
        const handleNewSuggestions = useCallback(() => {
            setResetSuggestionScroll(true)
        }, [setResetSuggestionScroll])

        useDetectNewItems(flattenedSuggestions, handleNewSuggestions)

        if (shouldShowLoadingCards) {
            return (
                <LoadingCardSkeletons data-testid="ant-skeleton" level={10} />
            )
        }

        if (isAssemblyElement || shouldShowEmpty) {
            return <NoChangesMessage />
        }

        return (
            //add accessibility role="list" to the container
            <div
                className={classes.container}
                role="region"
                aria-label="Track changes list"
                aria-live="polite"
                aria-atomic="false"
            >
                <VirtualizedSuggestionList
                    suggestions={flattenedSuggestions}
                    trackChangesData={trackChangesData}
                    isCustomFilterActive={isNotDefaultSuggestionCardFilter}
                    containerHeight={1000}
                />
            </div>
        )
    }
)
